import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Phone, PhoneOff, Mic, MicOff, Volume2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Hands-free phone-call style conversation with the AI citizen.
 * Pipeline:
 *   Microphone (always open)
 *     → VAD (volume-based) detects speech start/end
 *     → MediaRecorder captures the speech chunk
 *     → POST /api/transcribe (Whisper) returns text
 *     → POST /api/chat/message returns citizen text
 *     → POST /api/tts returns base64 mp3
 *     → <audio> plays — can be interrupted by next speech.
 *
 * Interruption (barge-in): if the dispatcher starts speaking while the citizen audio
 * is playing, we immediately pause the audio so they can talk over the citizen.
 *
 * Silence escalation (R8): if the dispatcher is silent for >SILENCE_PROMPT_MS while
 * idle, we trigger a is_silence_trigger=true backend message so the citizen reacts.
 */

const VOLUME_THRESHOLD = 0.025; // 0-1, calibrated for typical mic gain
const SPEECH_START_MS = 180;     // need this much voice above threshold to confirm start
const SILENCE_END_MS = 1100;     // this much silence after speech = end of utterance
const SILENCE_PROMPT_MS = 9000;  // dispatcher silence triggers citizen relance
const MIN_SPEECH_MS = 350;       // ignore micro-noise shorter than this

const PhoneCallMode = ({ open, onClose, sessionId, scenario, personality, language = 'french', onMessages }) => {
  const [callState, setCallState] = useState('idle'); // idle | listening | recording | processing | speaking
  const [muted, setMuted] = useState(false);
  const [transcript, setTranscript] = useState([]); // {speaker, text, ts}
  const [stressIndicator, setStressIndicator] = useState(0); // 0-100 visual hint
  const [callDuration, setCallDuration] = useState(0);

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const rafRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const speakingSinceRef = useRef(0);
  const silentSinceRef = useRef(0);
  const isRecordingRef = useRef(false);
  const lastDispatcherActivityRef = useRef(Date.now());
  const audioRef = useRef(null);
  const stateRef = useRef('idle');
  const mutedRef = useRef(false);
  const silenceTriggeredRef = useRef(false);
  const callStartRef = useRef(0);

  // Refs that mirror props — read inside rAF/MediaRecorder callbacks to avoid stale closures
  const sessionIdRef = useRef(sessionId);
  const personalityRef = useRef(personality);
  const languageRef = useRef(language);
  const scenarioRef = useRef(scenario);
  sessionIdRef.current = sessionId;
  personalityRef.current = personality;
  languageRef.current = language;
  scenarioRef.current = scenario;

  stateRef.current = callState;
  mutedRef.current = muted;

  const log = (speaker, text) => {
    setTranscript(prev => [...prev, { speaker, text, ts: Date.now() }]);
    if (typeof onMessages === 'function') onMessages(speaker, text);
  };

  // Start the call: open mic + start VAD loop
  const startCall = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      streamRef.current = stream;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      analyserRef.current = analyser;
      dataArrayRef.current = new Uint8Array(analyser.fftSize);
      callStartRef.current = Date.now();
      setCallState('listening');
      // In real 911 dispatching, the dispatcher speaks first ("911, what's your emergency?").
      // We do NOT auto-greet — the citizen waits until the dispatcher speaks.
      // Start the VAD loop
      vadTick();
    } catch (err) {
      console.warn('startCall mic permission failed:', err);
      toast.error("Impossible d'accéder au micro. Vérifiez les permissions.");
      onClose();
    }
  }, []);

  // VAD loop: analyses RMS energy each frame; manages MediaRecorder start/stop
  const vadTick = () => {
    const analyser = analyserRef.current;
    const data = dataArrayRef.current;
    if (!analyser || !data) return;

    analyser.getByteTimeDomainData(data);
    // RMS calculation 0..1
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / data.length);
    const now = Date.now();
    const speaking = rms > VOLUME_THRESHOLD && !mutedRef.current;

    if (speaking) {
      if (!speakingSinceRef.current) speakingSinceRef.current = now;
      silentSinceRef.current = 0;
      // Confirm speech start after SPEECH_START_MS of continuous voice
      if (!isRecordingRef.current && now - speakingSinceRef.current > SPEECH_START_MS) {
        // INTERRUPTION: if citizen is currently speaking, cut its audio
        if (audioRef.current && !audioRef.current.paused) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        startRecording();
      }
      lastDispatcherActivityRef.current = now;
      silenceTriggeredRef.current = false;
    } else {
      if (!silentSinceRef.current) silentSinceRef.current = now;
      speakingSinceRef.current = 0;
      // End of utterance: enough silence after recording started
      if (isRecordingRef.current && now - silentSinceRef.current > SILENCE_END_MS) {
        stopRecordingAndProcess();
      }
      // Dispatcher silence handling: when idle, if no activity for SILENCE_PROMPT_MS, prompt citizen
      if (stateRef.current === 'listening' && !silenceTriggeredRef.current
          && now - lastDispatcherActivityRef.current > SILENCE_PROMPT_MS) {
        silenceTriggeredRef.current = true;
        sendToCitizen('', true); // silence trigger
      }
    }

    setCallDuration(Math.floor((now - callStartRef.current) / 1000));
    rafRef.current = requestAnimationFrame(vadTick);
  };

  const startRecording = () => {
    try {
      const stream = streamRef.current;
      if (!stream) return;
      recordedChunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data); };
      mr.start();
      mediaRecorderRef.current = mr;
      isRecordingRef.current = true;
      setCallState('recording');
    } catch (err) {
      console.warn('startRecording failed:', err);
    }
  };

  const stopRecordingAndProcess = () => {
    const mr = mediaRecorderRef.current;
    if (!mr || mr.state === 'inactive') {
      isRecordingRef.current = false;
      setCallState('listening');
      return;
    }
    const speechDuration = Date.now() - (silentSinceRef.current - SILENCE_END_MS);
    mr.onstop = async () => {
      const blob = new Blob(recordedChunksRef.current, { type: 'audio/webm;codecs=opus' });
      isRecordingRef.current = false;
      // Discard noise/cough that's too short to be a sentence
      if (blob.size < 4000 || speechDuration < MIN_SPEECH_MS) {
        setCallState('listening');
        return;
      }
      setCallState('processing');
      try {
        const fd = new FormData();
        fd.append('audio', blob, 'utterance.webm');
        fd.append('language', languageRef.current === 'english' ? 'en' : 'fr');
        const r = await axios.post(`${API}/transcribe`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const text = (r.data?.text || '').trim();
        if (!text || text.length < 2) {
          setCallState('listening');
          return;
        }
        log('dispatcher', text);
        await sendToCitizen(text);
      } catch (err) {
        console.warn('Transcribe failed:', err);
        toast.error('Erreur de transcription');
        setCallState('listening');
      }
    };
    mr.stop();
  };

  const sendToCitizen = async (text, isSilenceTrigger = false) => {
    try {
      setCallState('processing');
      const currentSessionId = sessionIdRef.current;
      const currentPersonality = personalityRef.current;
      const currentLanguage = languageRef.current;
      const currentScenario = scenarioRef.current;
      if (!currentSessionId) {
        console.warn('[PhoneCall] sessionId is undefined — cannot send to citizen');
        toast.error('Session non démarrée');
        setCallState('listening');
        return;
      }
      const payload = {
        session_id: currentSessionId,
        message: text,
        scenario_context: currentScenario?.description || '',
        personality: currentPersonality,
        language: currentLanguage,
      };
      if (isSilenceTrigger) payload.is_silence_trigger = true;
      console.log('[PhoneCall] Sending to citizen:', { text: text.substring(0, 60), personality: currentPersonality, sessionId: currentSessionId });
      const chatResp = await axios.post(`${API}/chat/message`, payload);
      console.log('[PhoneCall] Chat response:', chatResp.data);
      const citizenText = chatResp.data?.response || chatResp.data?.message || '';
      if (!citizenText) {
        console.warn('[PhoneCall] No citizen text in chat response:', chatResp.data);
        if (chatResp.data?.error) toast.error(chatResp.data.error);
        setCallState('listening');
        return;
      }
      log('citizen', citizenText);
      console.log('[PhoneCall] Logged citizen reply. Transcript should update.');
      // Estimate stress visual from emotional cues
      setStressIndicator(estimateStress(citizenText));

      // TTS
      const voice = pickVoice(currentPersonality, currentScenario?.victim_info);
      const ttsResp = await axios.post(`${API}/tts`, { text: citizenText, voice, speed: 1.0 });
      const audioBase64 = ttsResp.data?.audio;
      if (!audioBase64) {
        setCallState('listening');
        return;
      }
      const audio = new Audio(`data:audio/mp3;base64,${audioBase64}`);
      audioRef.current = audio;
      audio.onplay = () => setCallState('speaking');
      audio.onended = () => setCallState('listening');
      audio.onerror = () => setCallState('listening');
      await audio.play();
    } catch (err) {
      console.warn('sendToCitizen failed:', err, err?.response?.data);
      const msg = err?.response?.data?.error || err?.message || 'Erreur de communication avec le citoyen';
      toast.error(msg);
      setCallState('listening');
    }
  };

  const pickVoice = (perso, _victim) => {
    // Deterministic voice selection that matches the existing chat (echo for male, shimmer for female)
    const personaToVoice = { panicked: 'shimmer', calm: 'echo', emotional: 'nova', confused: 'echo', angry: 'onyx' };
    return personaToVoice[perso] || 'echo';
  };

  const estimateStress = (txt) => {
    const t = (txt || '').toLowerCase();
    let s = 30;
    if (t.includes('!')) s += 15;
    if (/(s'?il vous plait|aidez-moi|au secours|please help)/i.test(t)) s += 25;
    if (/(\.\.\.|sang|mort|saign|inconscient|étouff|crise)/i.test(t)) s += 20;
    return Math.min(100, s);
  };

  const cleanup = () => {
    cancelAnimationFrame(rafRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) { /* noop */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      try { audioCtxRef.current.close(); } catch (e) { /* noop */ }
    }
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    }
    isRecordingRef.current = false;
    setCallState('idle');
  };

  useEffect(() => {
    if (open) {
      startCall();
    }
    return cleanup;
    // eslint-disable-next-line
  }, [open]);

  if (!open) return null;

  const stateLabel = {
    idle: 'En attente',
    listening: 'À l\'écoute…',
    recording: 'Vous parlez',
    processing: 'Traitement…',
    speaking: 'Citoyen parle',
  }[callState] || callState;

  const stateColor = {
    listening: '#24B219',
    recording: '#EE2130',
    processing: '#F4621C',
    speaking: '#2E6CF5',
    idle: '#6b7280',
  }[callState] || '#6b7280';

  const mins = String(Math.floor(callDuration / 60)).padStart(2, '0');
  const secs = String(callDuration % 60).padStart(2, '0');

  return (
    <div
      className="fixed z-50 bottom-6 right-6 w-[380px] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      style={{ maxHeight: 'calc(100vh - 48px)' }}
      data-testid="phone-call-overlay"
    >
        {/* Header */}
        <div className="px-5 py-4 text-white flex-shrink-0" style={{ background: 'linear-gradient(135deg, #002768 0%, #2E6CF5 100%)' }}>
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest opacity-75">Mode Téléphone</p>
              <h2 className="text-base font-bold truncate">{scenario?.title || 'Appel en cours'}</h2>
            </div>
            <div className="text-right ml-2">
              <p className="text-[10px] opacity-75">Durée</p>
              <p className="text-lg font-bold tabular-nums">{mins}:{secs}</p>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: stateColor }} />
            <span className="text-xs font-medium">{stateLabel}</span>
          </div>
        </div>

        {/* Stress meter */}
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Stress du citoyen</span>
            <span className="text-[10px] text-gray-700 font-mono">{stressIndicator}%</span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${stressIndicator}%`,
                backgroundColor: stressIndicator > 70 ? '#EE2130' : stressIndicator > 40 ? '#F4621C' : '#24B219',
              }}
            />
          </div>
        </div>

        {/* Transcript */}
        <div className="px-5 py-3 flex-1 overflow-y-auto space-y-2 min-h-[150px] max-h-[280px]" data-testid="phone-call-transcript">
          {transcript.length === 0 && (
            <div className="text-center text-gray-400 py-6">
              <Phone className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-xs">Parlez maintenant. Vous pouvez interrompre le citoyen à tout moment.</p>
            </div>
          )}
          {transcript.map((m, i) => (
            <div key={`${m.ts}-${i}`} className={`flex ${m.speaker === 'dispatcher' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`px-3 py-2 rounded-2xl max-w-[85%] text-xs ${
                  m.speaker === 'dispatcher'
                    ? 'bg-blue-600 text-white rounded-br-sm'
                    : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                }`}
              >
                <p className="font-semibold text-[9px] uppercase tracking-wider opacity-70 mb-0.5">
                  {m.speaker === 'dispatcher' ? 'Vous' : 'Citoyen'}
                </p>
                {m.text}
              </div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="px-5 py-3 bg-gray-50 flex items-center justify-center gap-3 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={() => setMuted(v => !v)}
            type="button"
            className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
              muted ? 'bg-red-100 text-red-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            data-testid="phone-call-mute-btn"
            title={muted ? 'Réactiver le micro' : 'Couper le micro'}
          >
            {muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>

          {/* Animated voice indicator */}
          <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: stateColor + '22' }}>
            <Volume2 className={`w-5 h-5 ${callState === 'speaking' ? 'animate-pulse' : ''}`} style={{ color: stateColor }} />
          </div>

          <button
            onClick={() => { cleanup(); onClose(); }}
            type="button"
            className="w-11 h-11 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition-all"
            data-testid="phone-call-hangup-btn"
            title="Raccrocher"
          >
            <PhoneOff className="w-5 h-5" />
          </button>
        </div>
    </div>
  );
};

export default PhoneCallMode;
